import React from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { SITE } from "@/data/config";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import CTABlock from "@/components/CTABlock";
import { organizationSchema, websiteSchema, webPageSchema, breadcrumbSchema } from "@/lib/schemas";

const Book = () => {
  useSeo({
    ...SEO.book,
    jsonLd: [
      organizationSchema(),
      websiteSchema(),
      webPageSchema({ path: "/book", name: SEO.book.title, description: SEO.book.description }),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Book", path: "/book" },
      ]),
    ],
  });
  return (
    <main data-testid="book-page" className="bg-black text-white">
      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-24">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "Book" }]} />
          <Overline className="mt-8">Book a call</Overline>
          <h1 className="mt-4 text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tight leading-[0.92]">
            Thirty minutes. One decision closer.
          </h1>
          <p className="mt-6 text-white/75 text-base lg:text-lg max-w-2xl leading-relaxed">
            A working call with Kostaja. Bring the loudest problem in your business right now. Leave with a plain-English recommendation for the strongest first move.
          </p>
          <p className="mt-4 text-white/60 text-sm lg:text-base max-w-2xl leading-relaxed">
            The strategy call is not a pressure funnel. It is meant to understand what is happening, identify the right level of help, and decide whether the next step is an audit, a fixed-scope service, a custom project, ongoing support, or a resource you can use yourself.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="book-open-calendly"
              className="inline-flex items-center justify-center bg-[#FFC600] text-black font-bold px-8 py-5 border-2 border-[#FFC600] hover:bg-black hover:text-[#FFC600] transition-colors text-base tracking-wide"
            >
              Open the calendar <span aria-hidden className="ml-2">↗</span>
            </a>
            <SecondaryButton to="/contact" testId="book-contact">
              Prefer to write first
            </SecondaryButton>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline className="mb-3">What to expect</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">A structured half hour.</h2>
          </div>
          <div className="lg:col-span-7">
            <ol className="border-l border-white/25">
              {[
                { k: "5 min", v: "You describe the business and the problem in plain English." },
                { k: "15 min", v: "We name the sequence: which piece to fix first, and why." },
                { k: "5 min", v: "We name the shape of the work: audit, fixed-scope, project, or resource." },
                { k: "5 min", v: "Written follow-up within one working day with a recommendation you can act on with or without hiring Kostaja." },
              ].map((it) => (
                <li key={it.k} className="pl-6 pb-6 relative">
                  <span className="absolute -left-[9px] top-1 inline-block w-4 h-4 bg-[#FFC600] border border-white/15" />
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold">{it.k}</div>
                  <p className="mt-1 text-white/85">{it.v}</p>
                </li>
              ))}
            </ol>
            <p className="mt-6 text-white/60 text-sm">
              Prefer a written first exchange?{" "}
              <Link to="/contact" className="underline underline-offset-4 hover:text-[#FFC600]">
                Send a message
              </Link>{" "}
              instead. Every message goes directly to studio leadership.
            </p>
          </div>
        </div>
      </section>

      <CTABlock variant="contact" />
    </main>
  );
};

export default Book;
